import { cantons } from './Canton'
import { type CantonId, mkCantonId } from './CantonId'

/**
 * Publication is the domain, without the TLD.
 *
 * @see parseHostname() function from the "@ta-interaktiv/parse-hostname" package
 */
export type Publication =
  | 'web'
  | 'firebaseapp'
  | '24heures'
  | 'bazonline'
  | 'berneroberlaender'
  | 'bernerzeitung'
  | 'bilan'
  | 'derbund'
  | 'landbote'
  | 'lematin'
  | 'langenthalertagblatt'
  | 'tagesanzeiger'
  | 'tdg'
  | 'thunertagblatt'
  | 'zsz'
  | 'zuonline'

export type PublicationMatcher<T> = { [k in Publication]: T }

/**
 * A version of 'casePublication' that accepts a string instead of 'Publication',
 * but also can return undefined if the string isn't one of the known publications.
 */
export const _casePublication = <T>(m: PublicationMatcher<T>, p: string): undefined | T => m[p as Publication]

/**
 * Maps the publication name (a string) to the preferred canton that should be shown
 * at the top of the list.
 */
const publicationCantonPreferenceList: PublicationMatcher<CantonId[]> = {
  '24heures': [
    mkCantonId('VD'),
    mkCantonId('GE'),
    mkCantonId('VS'),
    mkCantonId('FR'),
    mkCantonId('NE'),
    mkCantonId('JU'),
    mkCantonId('ZH'),
  ],
  berneroberlaender: [mkCantonId('BE')],
  bazonline: [mkCantonId('BS'), mkCantonId('BL')],
  bernerzeitung: [mkCantonId('BE')],
  bilan: [mkCantonId('VD'), mkCantonId('GE')],
  derbund: [mkCantonId('BE')],
  landbote: [mkCantonId('ZH')],
  langenthalertagblatt: [mkCantonId('BE')],
  thunertagblatt: [mkCantonId('BE')],
  lematin: [mkCantonId('GE')],
  tagesanzeiger: [mkCantonId('ZH')],
  tdg: [
    mkCantonId('GE'),
    mkCantonId('VD'),
    mkCantonId('VS'),
    mkCantonId('FR'),
    mkCantonId('NE'),
    mkCantonId('JU'),
    mkCantonId('ZH'),
  ],
  zsz: [mkCantonId('ZH')],
  zuonline: [mkCantonId('ZH')],

  // For testing, when accessing the app through the firebaseapp.com or web.app domains.
  web: [mkCantonId('ZH'), mkCantonId('ZG'), mkCantonId('GR')],
  firebaseapp: [mkCantonId('UR'), mkCantonId('AI'), mkCantonId('TI')],
}

/*
 * Return a list of 'CantonId's, based on the publication's preference.
 * (preferred cantons first, in the exact same order as specified in
 * 'publicationCantonPreferenceList', followed by the other cantons in
 * default order).
 */
export function publicationPreferredCantonIds(publication: string): CantonId[] {
  const preferredCantonIds = _casePublication(publicationCantonPreferenceList, publication)

  const cantonIds = cantons.map((x) => x.cantonId)

  if (preferredCantonIds) {
    return [...preferredCantonIds, ...cantonIds.filter((x) => !preferredCantonIds.includes(x))]
  } else {
    return cantonIds
  }
}
